import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';
import { cn, focusRing } from '../cn.js';

export const buttonVariants = cva(
  `inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-[var(--button-radius)] font-medium transition-[color,background-color,border-color,box-shadow,scale] duration-150 ease-out active:scale-[0.97] disabled:pointer-events-none disabled:opacity-50 [&_svg]:size-4 [&_svg]:shrink-0 ${focusRing}`,
  {
    variants: {
      variant: {
        primary: 'bg-accent text-accent-fg hover:bg-accent-hover',
        secondary: 'border border-border-strong bg-surface text-fg hover:bg-sunken',
        ghost: 'text-fg hover:bg-sunken',
        // Ação destrutiva em contorno: o único botão cheio da tela é o primário (o vermelho cheio competia com ele).
        danger: 'border border-danger/60 bg-surface text-danger hover:bg-danger-surface',
        link: 'text-accent-text underline-offset-4 hover:underline',
      },
      size: {
        md: 'h-[var(--button-height)] px-[var(--button-padding-x)] text-[length:var(--lr-text-base)]',
        sm: 'h-[var(--button-height-sm)] px-3 text-[length:var(--lr-text-sm)]',
        icon: 'size-[var(--button-height)]',
      },
    },
    defaultVariants: { variant: 'secondary', size: 'md' },
  },
);

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  /** Renderiza o filho (link, por exemplo) com a aparência de botão. */
  asChild?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(({ className, variant, size, asChild = false, type, ...props }, ref) => {
  const Comp = asChild ? Slot : 'button';
  return <Comp ref={ref} className={cn(buttonVariants({ variant, size }), className)} {...(asChild ? {} : { type: type ?? 'button' })} {...props} />;
});
Button.displayName = 'Button';
